import { useEffect, useState } from "react";
import { Target, TrendingUp, Users, Sparkles, Info, MousePointerClick } from "lucide-react";

const COMMISSION_PER_SUB = 100; // ~$100/mo per active WinBiggest subscriber
const GOAL_SUBS = 100;
const GOAL_MRR = COMMISSION_PER_SUB * GOAL_SUBS; // $10,000

interface Scenario {
  perMonth: number;
  addedMrr: number;
  months: number;
  label: string; // Getting Started / Consistent Growth / Accelerated Growth
  description: string;
}

const SCENARIOS: Scenario[] = [
  {
    perMonth: 2,
    addedMrr: 200,
    months: 50,
    label: "Getting Started",
    description:
      "A realistic pace for Brand Affiliates building consistency.",
  },
  {
    perMonth: 5,
    addedMrr: 500,
    months: 20,
    label: "Consistent Growth",
    description:
      "A sustainable pace that steadily builds recurring income.",
  },
  {
    perMonth: 10,
    addedMrr: 1000,
    months: 10,
    label: "Accelerated Growth",
    description:
      "Ideal for Brand Affiliates leveraging the full power of the WinCampaign DFY Business Growth Accelerator.",
  },
];

// Animated count-up hook (respects prefers-reduced-motion)
function useCountUp(target: number, durationMs = 900) {
  const [value, setValue] = useState(target);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setValue(target);
      return;
    }
    const start = performance.now();
    const from = value;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return value;
}

export default function ROI() {
  const [active, setActive] = useState(1); // default 5/mo
  const [progressKey, setProgressKey] = useState(0);
  const s = SCENARIOS[active];

  // Trigger progress animation replay whenever scenario changes
  useEffect(() => {
    setProgressKey((k) => k + 1);
  }, [active]);

  const animatedIncome = useCountUp(s.addedMrr);
  // Compute a fill duration inversely proportional to timeline for emotional pacing
  const fillDurationMs = s.months === 10 ? 1400 : s.months === 20 ? 2200 : 3200;

  return (
    <section
      id="roi"
      data-testid="wc-roi"
      className="relative bg-[#30265b] text-white py-24 sm:py-32 overflow-hidden wc-grain"
    >
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#673dfc]/40 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#d3fc3a]/10 blur-3xl" />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-[#d3fc3a] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-5">
            <Sparkles size={13} /> Let&apos;s make your $10K/month a reality
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.05]">
            Every new subscriber moves you closer to your{" "}
            <span className="text-[#d3fc3a]">$10K/month</span> goal.
          </h2>
          <div className="mt-6 space-y-4 text-white/75 leading-relaxed max-w-2xl">
            <p>
              The WinCampaign DFY Business Growth Accelerator isn&apos;t
              designed to help you make a one-time sale. It&apos;s designed
              to help you consistently grow your subscriber base — so you
              can build predictable recurring income month after month.
            </p>
            <p className="font-display font-semibold text-white">
              Imagine if your dedicated WinCampaign team consistently helped
              you add new subscribers every month.
            </p>
            <p>
              Click a monthly subscriber goal below to see how your recurring
              income could grow — and how quickly you could reach your
              $10K/month goal.
            </p>
          </div>
        </div>

        {/* Planner */}
        <div className="mt-12 sm:mt-14 grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          {/* Scenario picker */}
          <div className="lg:col-span-2 rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7 backdrop-blur-sm">
            {/* Try It Yourself callout — makes interactivity obvious */}
            <div className="rounded-2xl bg-[#d3fc3a]/10 border border-[#d3fc3a]/30 p-4 mb-5">
              <div className="flex items-center gap-2 text-[#d3fc3a] font-display font-bold text-sm">
                <MousePointerClick size={16} className="wc-nudge" />
                Try it yourself
              </div>
              <p className="mt-1.5 text-xs text-white/75 leading-relaxed">
                Click a monthly subscriber goal below to see how your
                recurring income and timeline change.
              </p>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="font-nav text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
                Choose your growth pace
              </div>
              <div className="hidden sm:flex items-center gap-1 text-[10px] font-nav text-white/40">
                <MousePointerClick size={11} /> Clickable
              </div>
            </div>

            <div className="space-y-3">
              {SCENARIOS.map((sc, i) => {
                const selected = i === active;
                return (
                  <button
                    key={sc.perMonth}
                    data-testid={`wc-roi-scenario-${sc.perMonth}`}
                    onClick={() => setActive(i)}
                    aria-pressed={selected}
                    className={`group w-full text-left rounded-2xl px-5 py-4 transition-all duration-300 border cursor-pointer ${
                      selected
                        ? "bg-[#d3fc3a] text-[#1a1a24] border-[#d3fc3a] shadow-[0_18px_40px_-12px_rgba(211,252,58,0.55)] -translate-y-0.5"
                        : "bg-white/5 text-white border-white/15 hover:border-[#d3fc3a]/60 hover:bg-white/[0.09] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#d3fc3a]/10"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-baseline gap-3">
                        <span
                          className={`font-display font-extrabold text-3xl leading-none ${
                            selected ? "text-[#1a1a24]" : "text-white"
                          }`}
                        >
                          +{sc.perMonth}
                        </span>
                        <span
                          className={`text-xs font-nav ${
                            selected ? "text-[#1a1a24]/70" : "text-white/60"
                          }`}
                        >
                          new subscribers / month
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-nav font-semibold uppercase tracking-[0.14em] whitespace-nowrap ${
                          selected
                            ? "bg-[#1a1a24] text-[#d3fc3a] px-2 py-0.5 rounded-full"
                            : "text-white/50 group-hover:text-[#d3fc3a]"
                        }`}
                      >
                        {selected ? "Selected" : "Tap to select"}
                      </span>
                    </div>
                    <div
                      className={`mt-2 font-display font-bold text-base ${
                        selected ? "text-[#1a1a24]" : "text-white"
                      }`}
                    >
                      {sc.label}
                    </div>
                    <p
                      className={`mt-1 text-xs leading-relaxed ${
                        selected ? "text-[#1a1a24]/70" : "text-white/55"
                      }`}
                    >
                      {sc.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Result panel */}
          <div className="lg:col-span-3 rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-8 backdrop-blur-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* MRR added */}
              <div className="rounded-2xl bg-[#d3fc3a] text-[#1a1a24] p-5 sm:p-6">
                <div className="flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#1a1a24]/70">
                  <TrendingUp size={13} /> Monthly recurring income added
                </div>
                <div
                  data-testid="wc-roi-monthly"
                  className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-none"
                >
                  +${animatedIncome.toLocaleString()}
                </div>
                <div className="mt-1 text-xs font-nav text-[#1a1a24]/70">
                  per month, every month
                </div>
              </div>

              {/* Time to 100 */}
              <div className="rounded-2xl bg-[#30265b] border border-white/10 p-5 sm:p-6">
                <div className="flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#c5b4ff]">
                  <Users size={13} /> Time to reach 100 subscribers
                </div>
                <div
                  data-testid="wc-roi-months"
                  className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-none text-white"
                >
                  ~{s.months} mo
                </div>
                <div className="mt-1 text-xs font-nav text-white/60">
                  at {s.perMonth} new subscribers / month
                </div>
              </div>
            </div>

            {/* Progress toward 100 subs */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs font-nav font-semibold uppercase tracking-[0.14em] text-white/60">
                  <Target size={13} /> Progress to 100 subscribers
                </div>
                <div className="text-xs font-nav text-white/60">
                  Goal:{" "}
                  <span className="text-[#d3fc3a] font-semibold">100 subs</span>
                </div>
              </div>
              <div
                data-testid="wc-roi-progress"
                className="relative h-3 rounded-full bg-white/10 overflow-hidden"
              >
                {/* Reset to 0 by remounting on scenario change (progressKey), then animate to 100 */}
                <div
                  key={progressKey}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c5b4ff] via-[#673dfc] to-[#d3fc3a] rounded-full"
                  style={{
                    animation: `wc-fill ${fillDurationMs}ms cubic-bezier(0.22,1,0.36,1) forwards`,
                  }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-nav text-white/50">
                <span>0 subs</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span className="text-[#d3fc3a] font-semibold">100 subs</span>
              </div>
            </div>

            {/* Long-term goal footer */}
            <div className="mt-6 rounded-2xl bg-white/[0.04] border border-[#673dfc]/40 p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#c5b4ff]">
                  Long-term goal
                </div>
                <div className="mt-1 font-display font-bold text-lg text-white leading-tight">
                  ${GOAL_MRR.toLocaleString()}/month recurring income
                </div>
              </div>
              <div className="hidden sm:block text-4xl">🎯</div>
            </div>
          </div>
        </div>

        {/* Why this matters */}
        <div className="mt-14 rounded-3xl bg-[#d3fc3a] text-[#1a1a24] p-8 sm:p-10">
          <div className="max-w-3xl">
            <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#1a1a24]/70">
              Why this matters
            </div>
            <h3 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl leading-[1.1]">
              Every subscriber you gain today can continue paying you tomorrow.
            </h3>
            <div className="mt-5 space-y-3 text-[15px] sm:text-base leading-relaxed text-[#1a1a24]/85">
              <p>
                The WinCampaign DFY Business Growth Accelerator isn&apos;t
                designed to help you generate one-time sales. It&apos;s
                designed to help you consistently build a recurring income
                business.
              </p>
              <p>
                Every new subscriber has the potential to continue generating
                recurring commissions month after month. The more consistently
                you grow your subscriber base, the sooner you can reach your
                goal of earning{" "}
                <span className="font-display font-bold">
                  $10,000/month in recurring income.
                </span>
              </p>
              <p className="font-display font-semibold text-[#1a1a24]">
                Instead of trying to do everything yourself, imagine having a
                dedicated marketing team working every month to help you reach
                that goal faster.
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 flex items-start gap-3 text-xs text-white/50 leading-relaxed max-w-3xl">
          <Info size={14} className="text-white/40 flex-shrink-0 mt-0.5" />
          <p>
            These projections are provided for illustration purposes only and
            assume an average Brand Affiliate recurring commission of
            approximately $100/month per active WinBiggest subscriber. Actual
            results will vary based on your sales activity, follow-up,
            subscriber retention, target market, and overall business
            performance.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes wc-fill {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes wc-nudge {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(1.5px, 1.5px); }
        }
        .wc-nudge { animation: wc-nudge 1.8s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
